"use client";

import { useEffect, useState } from "react";
import { z } from "zod";
import { useFetchApi } from "../../hooks/useFetchApi";

const AGENT_SETTINGS_PATH = "/api/systems-prompt";

const SavedAgentSettingsSchema = z
  .object({
    agentName: z.string().nullable(),
    chatSystemPrompt: z.string(),
  })
  .nullable();

type AgentSettingsLoadState = "loading" | "loaded" | "failed";

export function useAgentSettings(isOpen: boolean) {
  const { fetchWeb } = useFetchApi();
  const [agentName, setAgentName] = useState("");
  const [instructions, setInstructions] = useState("");
  const [loadState, setLoadState] = useState<AgentSettingsLoadState>("loading");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let isStale = false;
    setLoadState("loading");
    setSaveError(null);
    fetchWeb(AGENT_SETTINGS_PATH)
      .then((response) => {
        if (isStale) return;
        const saved = SavedAgentSettingsSchema.parse(response ?? null);
        setAgentName(saved?.agentName ?? "");
        setInstructions(saved?.chatSystemPrompt ?? "");
        setLoadState("loaded");
      })
      .catch(() => {
        if (!isStale) setLoadState("failed");
      });
    return () => {
      isStale = true;
    };
  }, [isOpen, fetchWeb]);

  async function saveAgentSettings(): Promise<boolean> {
    setIsSaving(true);
    setSaveError(null);
    try {
      await fetchWeb(AGENT_SETTINGS_PATH, {
        method: "POST",
        body: JSON.stringify({ agentName, chatSystemPrompt: instructions }),
      });
      return true;
    } catch {
      setSaveError("We could not save your agent settings. Please try again.");
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  const isFormDisabled = loadState !== "loaded" || isSaving;
  const errorMessage =
    loadState === "failed" ? "We could not load your agent settings. Close and try again." : saveError;

  return {
    agentName,
    setAgentName,
    instructions,
    setInstructions,
    isFormDisabled,
    errorMessage,
    saveAgentSettings,
  };
}
